import React, { useCallback, useEffect, useMemo, useState } from "react";

import PropTypes from "prop-types";

import Button from "@cayuse/shared-react.components.button";
import CovertButton from "@cayuse/shared-react.components.covert-button";
import Checkbox from "@cayuse/shared-react.components.form.inputs.checkbox";
import Icon from "@cayuse/shared-react.components.icon";
import IconButton from "@cayuse/shared-react.components.icon-button";
import { isFunction, isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";
import SearchDropdown from "@cayuse/shared-react.components.search-dropdown";

import usePersonSearch from "components/flowable/FlowablePersonSearch/components/hooks/usePersonSearch";
import { useTeamAccess } from "hooks";

import Subsection from "components/Subsection";
import Table from "components/Table";
import { COLUMN_HEADER_KEYS } from "globals/constants";
import { notifyError } from "utils";

import styles from "./styles.css";

const TeamMemberAssignment = ({
	canEdit,
	dictionary,
	error,
	formId,
	formType,
	loading,
	onSave,
	teamMembers
}) => {
	const [ isSaving, setIsSaving ] = useState(false);
	const [ hasUnsavedChanges, setHasUnsavedChanges ] = useState(false);
	const [ isReadOnly, setIsReadOnly ] = useState(true);
	const [ isAddPerson, setIsAddPerson ] = useState(false);
	const [ userToAdd, setUserToAdd ] = useState(null);
	const [ canNewUserEdit, setCanNewUserEdit ] = useState(false);

	const teamAccess = useTeamAccess(teamMembers, formType, formId);
	const hasTeamMembers = isNonEmptyArray(teamAccess.members);

	const { search } = usePersonSearch();

	const removeSelectedMemberFromTeam = useCallback(() => {
		if (userToAdd) {
			teamAccess.removeMember(userToAdd.person.personId);
			setUserToAdd(null);
		}
	}, [ teamAccess, userToAdd ]);

	const onTeamMemberSelected = useCallback((value, selectedOptions) => {
		const selectedOption = selectedOptions[0];

		removeSelectedMemberFromTeam();
		
		if(selectedOption) {
			teamAccess.addMember(selectedOption?.person, { canEdit: canNewUserEdit });

			setUserToAdd(selectedOption);
		}

		setHasUnsavedChanges(true);
	}, [ teamAccess, canNewUserEdit, removeSelectedMemberFromTeam ]);

	const onAddClick = useCallback(() => {
		if (canEdit) {
			setIsReadOnly(!isReadOnly);
			setIsAddPerson(!isAddPerson);
		}
	}, [ canEdit, isAddPerson, isReadOnly ]);

	const onCancelClick = useCallback(() => {
		if (!isReadOnly) {
			setIsReadOnly(true);
		}

		if (isAddPerson) {
			setIsAddPerson(false);
			setUserToAdd(null);
		}

		removeSelectedMemberFromTeam();
		teamAccess.resetChanges();
		setHasUnsavedChanges(false);
	}, [ isReadOnly, isAddPerson, teamAccess, removeSelectedMemberFromTeam ]);

	const onEditClick = useCallback(() => {
		if (canEdit) {
			setIsReadOnly(!isReadOnly);
		}
	}, [ canEdit, isReadOnly ]);

	const toggleMemberCanEdit = (member, canEdit) => {
		if (!isReadOnly) {
			teamAccess.setCanMemberEdit(member, canEdit);
			setHasUnsavedChanges(true);
		}
	};

	const onRemoveTeamMember = useCallback(memberId => {
		teamAccess.removeMember(memberId);
		setHasUnsavedChanges(true);
	}, [ teamAccess ]);

	const onSaveClick = useCallback(() => {
		setIsSaving(true);
		teamAccess.save()
			.then(() => {
				setIsReadOnly(true);
				setIsAddPerson(false);
				setUserToAdd(null);
				setCanNewUserEdit(false);
				setHasUnsavedChanges(false);

				if (isFunction(onSave)) {
					onSave({ teamAccess });
				}
			})
			.catch((error) => {
				notifyError(
					dictionary.errors?.unableToSave
						|| "There was an error and changes to the team were not saved. Please try saving the team again.",
					error
				);
			})
			.finally(() => setIsSaving(false));
	}, [ teamAccess, dictionary.errors, onSave, setHasUnsavedChanges ]);

	const getDataPromise = useCallback(searchText => 
		search(searchText),
    [userToAdd?.value]);

	const topButtons = useMemo(() => {
		const buttonOne = () => {
			if(!isReadOnly) {
				return (
					<Button secondary onClick={onCancelClick}>
						{ dictionary.buttonLabels?.cancel || "Cancel" }
					</Button>
				);
			}

			return (
				<IconButton icon="pencil-alt" labelPosition="left" disabled={!canEdit} onClick={onEditClick}> {/* todo: add variant="regular" once Icons support FA-Pro */}
					{ dictionary.buttonLabels?.edit || "Edit" }
				</IconButton>
			);
		};

		const buttonTwo = () => {
			if(!isReadOnly) {
				return (
					<IconButton icon="save"
					            labelPosition="left"
					            onClick={onSaveClick}
					            busy={isSaving}
					            disabled={!hasUnsavedChanges}
					>
						{ dictionary.buttonLabels?.save || "Save" }
					</IconButton>
				);
			}

			return (
				<IconButton icon="plus" labelPosition="left" disabled={!canEdit} onClick={onAddClick}>
					{ dictionary.buttonLabels?.add || "Add Person" }
				</IconButton>
			);
		};

		return ([
			buttonOne(),
			buttonTwo()
		]);
	}, [
		canEdit,
		dictionary.buttonLabels,
		hasUnsavedChanges,
		isReadOnly,
		isSaving,
		onAddClick,
		onCancelClick,
		onEditClick,
		onSaveClick
	]);

	const headers = useMemo(() => {
		return [
			{
				label: dictionary.columnHeaders?.person || "Person",
				className: styles.nameCol
			},
			{
				label: dictionary.columnHeaders?.email || "Email"
			},
			{
				label: dictionary.columnHeaders?.view || "View",
				className: styles.inputColumn
			},
			{
				label:dictionary.columnHeaders?.edit,
				className: styles.inputColumn
			},
		];
	}, [ dictionary.columnHeaders ]);

	useEffect(() => {
		if (userToAdd) {
			teamAccess.setCanMemberEdit(userToAdd.person, canNewUserEdit);
		}
	}, [ teamAccess, canNewUserEdit, userToAdd ]);

	const renderRow = teamMember => {
		const hasEditPermission = teamMember.canUpdate;

		return teamMember
			? (
				<Table.Row key={teamMember.fullName}>
					<Table.Cell className={styles.nameCol}>
						{teamMember.fullName}
					</Table.Cell>

					<Table.Cell>
						{teamMember.email || teamMember.contactEmail} {/* account for inconsistent naming between admin and sp */}
					</Table.Cell>

					<Table.Cell className={styles.inputColumn}>
						<Checkbox label="" disabled checked />
					</Table.Cell>

					<Table.Cell className={`${isReadOnly ? styles.inputColumn : ""}`}>
						<Checkbox label=""
						          checked={hasEditPermission}
						          disabled={isReadOnly}
						          onClick={() => toggleMemberCanEdit(teamMember, !hasEditPermission)}
						/>
						{!isReadOnly &&
						<CovertButton className={styles.trashIcon} aria-label={`Delete ${teamMember.fullName}`} onClick={() => onRemoveTeamMember(teamMember)}>
							<Icon name="trash" size="medium" />
						</CovertButton>
						}
					</Table.Cell>

				</Table.Row>
			)
			: "";
	};

	const renderAddPerson = () => {
		return (
			<Table.Row>
				<Table.Cell className={styles.nameCol}>
					<SearchDropdown
						value={userToAdd?.value || null}
						search={(options) => options}
						selection
						selectOnNavigation={false}
						forceSelection={false}
						selectOnBlur={false}
						onGetDataPromise={getDataPromise}
						searchingMessage={"Searching"} // todo: dictionary
						placeholder={"Add member"} // todo: dictionary
						onChange={onTeamMemberSelected}
						narrowCriteriaHint={{show: true}}
						className="personFinder"	
						fieldLabel="Add member"			
					/>
				</Table.Cell>

				<Table.Cell>
					{userToAdd?.person?.contactEmail || "--"}
				</Table.Cell>

				<Table.Cell className={styles.inputColumn}>
					<Checkbox label="" disabled checked /> {/* Always disabled per UI spec, delete user instead */}
				</Table.Cell>

				<Table.Cell>
					<Checkbox label=""
					          disabled={isReadOnly}
					          onClick={() => setCanNewUserEdit(!canNewUserEdit)}
					/>
				</Table.Cell>

			</Table.Row>
		);
	};

	return (
		<div data-component="Fragments\TeamMemberAssignment">
			<Subsection title={dictionary.title || "Access"} buttons={topButtons}>
				<div className={styles.instructions}>
					{dictionary.instructions || "Manage  who can view or edit the team."}
				</div>

				<Table className={styles.teamTable} isLoading={loading} error={error} overflow="visible" captionText="Team Member Assignment">
					<Table.Header>
						<Table.Row>
							{ headers.map(({ label, ...rest }) => (
								<Table.HeaderCell key={label} {...rest}>
									{ label }
								</Table.HeaderCell>
							))}
						</Table.Row>
					</Table.Header>

					<Table.Body>
						{isAddPerson && renderAddPerson()}
						{hasTeamMembers || isAddPerson
							? teamAccess.members
								.filter(({ personId }) => personId !== userToAdd?.person?.personId)
								.map(renderRow)
							: (
								<Table.ErrorRow>
									{ dictionary.errors?.noTeamMembers || "No team members have been assigned." }
								</Table.ErrorRow>
							)
						}
					</Table.Body>
				</Table>
			</Subsection>
		</div>
	);
};

TeamMemberAssignment.propTypes = {
	canEdit: PropTypes.bool,
	dictionary: PropTypes.shape({
		buttonLabels: PropTypes.shape({
			edit: PropTypes.string,
			add: PropTypes.string,
			cancel: PropTypes.string,
			save: PropTypes.string
		}),
		columnHeaders: PropTypes.shape({
			person: PropTypes.string,
			email: PropTypes.string,
			view: PropTypes.string,
			edit: PropTypes.string,
		}),
		errors: PropTypes.shape({
			noTeamMembers: PropTypes.string,
			unableToSave: PropTypes.string
		})
	}),
	error: PropTypes.oneOfType([
		PropTypes.bool,
		PropTypes.string
	]),
	formId: PropTypes.string.isRequired,
	formType: PropTypes.oneOf(Object.values(COLUMN_HEADER_KEYS)).isRequired,
	loading: PropTypes.bool,
	teamMembers: PropTypes.arrayOf(
		PropTypes.shape({
			fullName: PropTypes.string,
			email: PropTypes.string
		})
	)
};

TeamMemberAssignment.defaultProps = {
	canEdit: false
};

export default TeamMemberAssignment;
